import type { Condition } from '@seen/core/observation-logic/conditionSchema'
import {
  type ConditionValue,
  isConditionalWrapper,
  isSectionLike,
  OBSERVATION_NODE,
  OBSERVATION_PANEL_NODE,
  readCondition,
} from '@seen/core/observation-logic/documentUnits'
import type { InstrumentAnswers } from '@seen/core/observation-logic/instruments/records'
import type { ShowWhenResult } from '@seen/core/observation-logic/showWhen'
import { observationReferenceStrings } from '@seen/core/observationReferences'
import { Extension } from '@tiptap/core'
import type { Node as PMNode } from '@tiptap/pm/model'
import { type EditorState, Plugin, PluginKey } from '@tiptap/pm/state'
import {
  Decoration,
  type DecorationAttrs,
  DecorationSet,
  type EditorView,
} from '@tiptap/pm/view'
import {
  collectConditionalUnits,
  conditionDescriber,
  evaluateDocument,
  type PanelAnswers,
  triState,
} from '#src/components/rich-text-editor/conditional-display/conditionalDisplayState.ts'
import type { ConditionDecorationSpec } from '#src/components/rich-text-editor/ObservationNode/observationNodeCondition.ts'
import { REGISTRY_VARIABLE_SCAN_META } from '#src/components/rich-text-editor/registry-variable/registryVariableResolution.ts'

export interface ConditionalDisplayOptions {
  // Behind the structuredCapture flag; off, the extension does nothing.
  enabled: boolean
  // A template hides nothing and names each condition in a chip instead.
  isTemplate: boolean
  /* Takes a hidden node's answer off the chart before the banner's Clear
   * drops it from the node; rejects when the records could not be removed. */
  removeRecords?: (references: string[]) => Promise<void>
}

export interface ConditionalDisplayState {
  values: ReadonlyMap<string, ConditionValue>
  // Whether the host has fed in the recorded values yet. Until it has, an
  // absent value reads as unanswered, not as not known yet.
  fed: boolean
  panelAnswers: PanelAnswers
  result: ShowWhenResult
  // Words a condition as the note shows it.
  describe: (condition: Condition) => string
  // Nodes shown for now despite their condition, to see an answer they hold.
  revealed: ReadonlySet<string>
  decorations: DecorationSet
}

interface ConditionalDisplayMeta {
  values?: ReadonlyMap<string, ConditionValue>
  panel?: { id: string; answers: InstrumentAnswers }
  reveal?: string
}

export const CONDITIONAL_DISPLAY_KEY = new PluginKey<ConditionalDisplayState>(
  'conditionalDisplay'
)

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    conditionalDisplay: {
      // The recorded values conditions read, by `conditionValueKey`.
      setConditionValues: (
        values: ReadonlyMap<string, ConditionValue>
      ) => ReturnType
      // The answers an instrument panel holds ahead of writing them.
      setPanelAnswers: (
        panelId: string,
        answers: InstrumentAnswers
      ) => ReturnType
      revealConditional: (id: string) => ReturnType
    }
  }
}

// Shows and hides observation nodes and conditional sections by their
// conditions, from the recorded values the host feeds in and the editor's
// registry contexts. Live, a hidden one is hidden (an answer it holds says
// so); in a template, nothing hides and each condition shows as a chip.
export const ConditionalDisplayExtension =
  Extension.create<ConditionalDisplayOptions>({
    name: 'conditionalDisplay',

    addOptions() {
      return { enabled: false, isTemplate: false, removeRecords: undefined }
    },

    addCommands() {
      return {
        setConditionValues:
          (values) =>
          ({ tr, dispatch }) => {
            if (dispatch) {
              tr.setMeta(CONDITIONAL_DISPLAY_KEY, { values })
              tr.setMeta('addToHistory', false)
            }
            return true
          },
        setPanelAnswers:
          (panelId, answers) =>
          ({ tr, dispatch }) => {
            if (dispatch) {
              tr.setMeta(CONDITIONAL_DISPLAY_KEY, {
                panel: { id: panelId, answers },
              })
              tr.setMeta('addToHistory', false)
            }
            return true
          },
        revealConditional:
          (id) =>
          ({ tr, dispatch }) => {
            if (dispatch) {
              tr.setMeta(CONDITIONAL_DISPLAY_KEY, { reveal: id })
              tr.setMeta('addToHistory', false)
            }
            return true
          },
      }
    },

    addProseMirrorPlugins() {
      if (!this.options.enabled) {
        return []
      }
      const { isTemplate, removeRecords } = this.options
      const editor = this.editor
      const contexts = () => editor.storage.registryVariable?.contexts ?? {}

      const compute = (
        doc: PMNode,
        values: ReadonlyMap<string, ConditionValue>,
        fed: boolean,
        panelAnswers: PanelAnswers,
        revealed: ReadonlySet<string>
      ): ConditionalDisplayState => {
        const units = collectConditionalUnits(doc, values, panelAnswers)
        const result = evaluateDocument(units, contexts())
        const describe = conditionDescriber(units)
        return {
          values,
          fed,
          panelAnswers,
          result,
          describe,
          revealed,
          decorations: decorate(
            doc,
            result,
            revealed,
            isTemplate,
            describe,
            removeRecords
          ),
        }
      }

      return [
        new Plugin<ConditionalDisplayState>({
          key: CONDITIONAL_DISPLAY_KEY,
          state: {
            init: (_, state) =>
              compute(state.doc, new Map(), false, new Map(), new Set()),
            apply: (tr, previous, _old, next) => {
              const meta = tr.getMeta(CONDITIONAL_DISPLAY_KEY) as
                | ConditionalDisplayMeta
                | undefined
              const rescan = Boolean(tr.getMeta(REGISTRY_VARIABLE_SCAN_META))
              if (!meta && !rescan && !tr.docChanged) {
                return previous
              }
              const revealed = meta?.reveal
                ? new Set([...previous.revealed, meta.reveal])
                : previous.revealed
              const panelAnswers = meta?.panel
                ? new Map([
                    ...previous.panelAnswers,
                    [meta.panel.id, meta.panel.answers],
                  ])
                : previous.panelAnswers
              return compute(
                next.doc,
                meta?.values ?? previous.values,
                previous.fed || !!meta?.values,
                panelAnswers,
                revealed
              )
            },
          },
          props: {
            decorations: (state) =>
              CONDITIONAL_DISPLAY_KEY.getState(state)?.decorations ??
              DecorationSet.empty,
          },
          // Every observation and section carries an id conditions settle
          // on: given when missing, and anew when pasted twice.
          appendTransaction: (transactions, _old, state) => {
            if (
              !editor.isEditable ||
              !transactions.some((transaction) => transaction.docChanged)
            ) {
              return null
            }
            return withIds(state)
          },
        }),
      ]
    },
  })

function withIds(state: EditorState) {
  const seen = new Set<string>()
  const missing: number[] = []
  state.doc.descendants((node, pos) => {
    const named =
      node.type.name === OBSERVATION_NODE || isSectionLike(node.type.name)
    if (!named) {
      return true
    }
    const id = node.attrs.id as string | null
    if (!id || seen.has(id)) {
      missing.push(pos)
    } else {
      seen.add(id)
    }
    return isConditionalWrapper(node.type.name)
  })
  if (!missing.length) {
    return null
  }
  const tr = state.tr
  for (const pos of missing) {
    tr.setNodeAttribute(pos, 'id', crypto.randomUUID())
  }
  return tr.setMeta('addToHistory', false)
}

function decorate(
  doc: PMNode,
  result: ShowWhenResult,
  revealed: ReadonlySet<string>,
  isTemplate: boolean,
  describe: (condition: Condition) => string,
  removeRecords: ConditionalDisplayOptions['removeRecords']
): DecorationSet {
  if (!result.nodes.size && !result.sections.size) {
    return DecorationSet.empty
  }
  const decorations: Decoration[] = []
  const cycles = new Set(result.cycles)
  doc.descendants((node, pos) => {
    const isSection = isSectionLike(node.type.name)
    const isObservation = node.type.name === OBSERVATION_NODE
    if ((!isSection && !isObservation) || !node.attrs.id) {
      return true
    }
    const id = node.attrs.id as string
    const state = triState(
      isSection ? result.sections.get(id) : result.nodes.get(id)
    )
    const hide =
      !isTemplate && state === 'hidden' && !(isObservation && revealed.has(id))
    // An instrument is marked like an observation: its frame shows the glyph.
    const condition =
      isObservation || node.type.name === OBSERVATION_PANEL_NODE
        ? readCondition(node.attrs.showWhen)
        : undefined
    // In a template a conditional node is outlined and marked by a glyph
    // whose tooltip words the condition, rather than a chip of text.
    const marked = isTemplate && !!condition
    const cycle = cycles.has(`node:${id}`)
    const text = condition ? describe(condition) : ''
    const spec: ConditionDecorationSpec | undefined = marked
      ? { conditionText: text, conditionCycle: cycle }
      : undefined
    const attrs: DecorationAttrs = { 'data-condition': state }
    if (hide) {
      attrs.class = 'hidden'
    }
    if (marked) {
      attrs['data-condition-text'] = text
      attrs.class = cycle
        ? 'rounded-xs outline-dashed outline-1 outline-offset-1 outline-red-400'
        : 'rounded-xs outline-dashed outline-1 outline-offset-1 outline-gray-450'
    }
    decorations.push(Decoration.node(pos, pos + node.nodeSize, attrs, spec))
    if (isObservation && condition) {
      if (isTemplate) {
        // The node's frame shows the glyph, from the spec above.
      } else if (state === 'unknown') {
        decorations.push(
          Decoration.widget(pos, () => unknownHint(text), {
            side: -1,
            key: `unknown:${id}:${text}`,
            ignoreSelection: true,
          })
        )
      } else if (hide && node.attrs.reference) {
        decorations.push(
          Decoration.widget(
            pos,
            (view) => hiddenAnswerBanner(view, id, removeRecords),
            {
              side: -1,
              key: `hidden-answer:${id}`,
              ignoreSelection: true,
            }
          )
        )
      }
    }
    return isSection
  })
  return DecorationSet.create(doc, decorations)
}

function unknownHint(text: string): HTMLElement {
  const element = document.createElement('span')
  element.className = 'mr-1 text-muted-foreground text-xs'
  element.contentEditable = 'false'
  element.title = `Shown, since this cannot be told yet: ${text}`
  element.textContent = `(${text}: unknown)`
  return element
}

// Said beside a node its condition hides while it holds an answer: shown
// for now, or the answer taken off it.
function hiddenAnswerBanner(
  view: EditorView,
  id: string,
  removeRecords: ConditionalDisplayOptions['removeRecords']
): HTMLElement {
  const element = document.createElement('span')
  element.className =
    'mr-1 inline-flex items-center gap-1 rounded-xs bg-amber-50 px-1 text-amber-800 text-xs'
  element.contentEditable = 'false'
  element.dataset.hiddenAnswer = id
  element.append('Hidden by a condition but holds an answer')
  const button = (label: string, onClick: () => void): HTMLButtonElement => {
    const control = document.createElement('button')
    control.type = 'button'
    control.className = 'underline hover:no-underline'
    control.textContent = label
    control.addEventListener('mousedown', (event) => event.preventDefault())
    control.addEventListener('click', onClick)
    return control
  }
  element.append(
    ' ',
    button('Reveal', () =>
      view.dispatch(
        view.state.tr
          .setMeta(CONDITIONAL_DISPLAY_KEY, { reveal: id })
          .setMeta('addToHistory', false)
      )
    ),
    ' ',
    button('Clear', () => {
      const found = findObservation(view, id)
      if (!found) {
        return
      }
      const references = observationReferenceStrings(found.node.attrs)
      // The record leaves the chart first, so the node never lets go of an
      // answer that is still recorded.
      void (removeRecords?.(references) ?? Promise.resolve())
        .then(() => {
          const current = view.isDestroyed
            ? undefined
            : findObservation(view, id)
          if (!current) {
            return
          }
          view.dispatch(
            view.state.tr
              .setNodeAttribute(current.pos, 'reference', null)
              .setNodeAttribute(current.pos, 'references', null)
          )
        })
        .catch(() => {})
    })
  )
  return element
}

function findObservation(
  view: EditorView,
  id: string
): { node: PMNode; pos: number } | undefined {
  let found: { node: PMNode; pos: number } | undefined
  view.state.doc.descendants((node, pos) => {
    if (node.type.name === OBSERVATION_NODE && node.attrs.id === id) {
      found = { node, pos }
    }
    return found === undefined
  })
  return found
}
